import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  Ban,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  FileSignature,
  Gavel,
  ListChecks,
  MapPin,
  Megaphone,
  Plus,
  Save,
  Trash2,
  Users as UsersIcon,
} from "lucide-react";
import { Avatar, Button, Dialog, Input, Pill, toast } from "../ui";
import {
  governanceApi,
  errMessage,
  formatDateTime,
  isoToLocalInput,
  localInputToIso,
  ISSUE_KIND_META,
  MEETING_KIND_META,
  MEETING_STATUS_META,
  RESOLUTION_STATUS_META,
  RESOLUTION_ORIGIN_META,
  RSVP_CHANNEL_LABELS,
} from "./api";
import { RsvpTallyBar } from "./GovernancePage";
import TrailTimeline from "./TrailTimeline";
import "./MeetingDetailPage.css";

const AGENDA_KINDS = [
  { value: "ISSUE", label: "Issue (topic / request / dispute)" },
  { value: "QUERY", label: "Query to be addressed" },
  { value: "BUDGET", label: "Budget approval" },
  { value: "OTHER", label: "Other" },
];

export default function MeetingDetailPage() {
  const { id } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState("");
  const [trailKey, setTrailKey] = useState(0);

  const load = useCallback(() => {
    governanceApi
      .getMeeting(id)
      .then((d) => {
        setMeeting(d);
        setError("");
      })
      .catch((e) => setError(errMessage(e)));
    governanceApi
      .overview()
      .then(setOverview)
      .catch(() => {});
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Every mutation endpoint returns the fresh Meeting(detail).
  const apply = useCallback((next) => {
    setMeeting(next);
    setTrailKey((k) => k + 1);
  }, []);

  if (error && !meeting) {
    return (
      <div className="vsgov-shell">
        <BackLink />
        <div className="vsgov-error">{error}</div>
      </div>
    );
  }
  if (!meeting) {
    return (
      <div className="vsgov-shell">
        <BackLink />
        <div className="vsgov-loading">Loading meeting…</div>
      </div>
    );
  }

  const me = overview?.me || {};
  const kindMeta =
    MEETING_KIND_META[meeting.kind] || { label: meeting.kind, pill: "neutral" };
  const statusMeta =
    MEETING_STATUS_META[meeting.status] || { label: meeting.status, pill: "neutral" };
  const isDraft = meeting.status === "DRAFT";
  const noticed = ["NOTICE_ISSUED", "RESCHEDULED"].includes(meeting.status);
  const terminal = ["HELD", "CANCELLED"].includes(meeting.status);

  return (
    <div className="vsgov-shell vsgov-meeting-detail">
      <BackLink />

      <div className="vsgov-detail-head">
        <div className="vsgov-card-pills">
          <Pill variant={kindMeta.pill}>{kindMeta.label}</Pill>
          <Pill variant={statusMeta.pill}>{statusMeta.label}</Pill>
          {meeting.reschedule_count > 0 && (
            <Pill variant="warning">
              Rescheduled ×{meeting.reschedule_count}
            </Pill>
          )}
        </div>
        <h1>{meeting.title}</h1>
        {meeting.description && (
          <p className="vsgov-meeting-desc">{meeting.description}</p>
        )}
        <div className="vsgov-mmeta">
          <span>
            <CalendarDays size={14} /> {formatDateTime(meeting.scheduled_at)}
          </span>
          <span>
            <MapPin size={14} /> {meeting.venue || "Venue TBC"}
          </span>
          {meeting.notice_issued_at && (
            <span>
              <Megaphone size={14} /> Notice issued{" "}
              {formatDateTime(meeting.notice_issued_at)}
            </span>
          )}
          {meeting.held_at && (
            <span>
              <CheckCircle2 size={14} /> Held {formatDateTime(meeting.held_at)}
            </span>
          )}
        </div>
      </div>

      {isDraft && meeting.can_manage && (
        <DraftWorkbench
          meeting={meeting}
          eligible={meeting.rsvp?.eligible ?? overview?.eligible_members ?? 0}
          onChanged={apply}
        />
      )}

      {/* Draft: the board prepares the resolutions that will be circulated
          with the notice (Option A — they're locked once the notice is out). */}
      {isDraft && meeting.can_manage && (
        <CirculatedResolutions
          meeting={meeting}
          me={me}
          onChanged={apply}
          mode="draft"
        />
      )}

      {isDraft && meeting.can_manage && (
        <Polls meeting={meeting} me={me} onChanged={apply} />
      )}

      {/* Announced/concluded: the invitation members see — the agenda and the
          resolutions they are asked to vote on, presented as one notice. */}
      {!isDraft && (
        <section className="vsgov-section vsgov-notice">
          <h2 className="vsgov-section-title">
            <Megaphone size={15} /> Meeting notice
          </h2>
          {(meeting.agenda || []).length > 0 && (
            <div className="vsgov-notice-block">
              <h3 className="vsgov-subhead">
                <ListChecks size={14} /> Agenda
              </h3>
              <AgendaList items={meeting.agenda} />
            </div>
          )}
          <CirculatedResolutions
            meeting={meeting}
            me={me}
            onChanged={apply}
            mode="notice"
          />
        </section>
      )}

      {!isDraft && <Polls meeting={meeting} me={me} onChanged={apply} />}

      {meeting.notice_issued_at && (
        <RsvpSection
          meeting={meeting}
          canRsvp={!!me.is_member && noticed}
          onChanged={apply}
        />
      )}

      {/* Resolutions raised on the floor during the meeting, recorded by the
          board (show of hands) — no online voting. */}
      {!isDraft && (
        <FloorResolutions meeting={meeting} me={me} onChanged={apply} />
      )}

      {meeting.can_manage && !terminal && !isDraft && (
        <BoardControls meeting={meeting} onChanged={apply} />
      )}

      <TrailTimeline targetId={meeting.id} refreshKey={trailKey} />
    </div>
  );
}

// Advisory polls — "sense of the house" on agenda items. Members vote on an
// option; the board can add polls (draft or live) and close them. Distinct
// from the binding For/Against resolution above.
function Polls({ meeting, me, onChanged }) {
  const polls = meeting.polls || [];
  const canVote = !!me.is_member;
  const canManage = !!me.is_board;
  const [busy, setBusy] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);

  const refresh = async () => {
    const next = await governanceApi.getMeeting(meeting.id);
    onChanged(next);
  };

  const vote = async (pollId, optionId) => {
    if (busy) return;
    setBusy(`v-${optionId}`);
    try {
      await governanceApi.votePoll(pollId, optionId);
      await refresh();
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const closePoll = async (pollId) => {
    if (busy) return;
    setBusy(`c-${pollId}`);
    try {
      await governanceApi.closePoll(pollId);
      await refresh();
      toast.success("Poll closed.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const create = async () => {
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (title.trim().length < 1 || opts.length < 2) {
      toast.error("Add a title and at least two options.");
      return;
    }
    setBusy("create");
    try {
      await governanceApi.createPoll(meeting.id, {
        title: title.trim(),
        question: question.trim() || undefined,
        options: opts,
      });
      await refresh();
      setTitle("");
      setQuestion("");
      setOptions(["", ""]);
      setFormOpen(false);
      toast.success("Poll added.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  if (polls.length === 0 && !canManage) return null;

  return (
    <section className="vsgov-section">
      <h2 className="vsgov-section-title">
        <BarChart3 size={15} /> Polls
        <span className="vsgov-section-hint">advisory — sense of the house</span>
      </h2>

      {polls.map((p) => {
        const total = p.total_votes || 0;
        const open = p.status === "OPEN";
        return (
          <div key={p.id} className="vsgov-poll">
            <div className="vsgov-poll-head">
              <strong>{p.title}</strong>
              {!open && <Pill variant="neutral">Closed</Pill>}
            </div>
            {p.question && <p className="vsgov-poll-q">{p.question}</p>}
            <div className="vsgov-poll-opts">
              {p.options.map((o) => {
                const pct = total > 0 ? Math.round((o.votes / total) * 100) : 0;
                const mine = p.my_option_id === o.id;
                const voters = o.voters || [];
                return (
                  <div key={o.id} className="vsgov-poll-optwrap">
                    <button
                      className={`vsgov-poll-opt ${mine ? "mine" : ""}`}
                      disabled={!open || !canVote || busy === `v-${o.id}`}
                      onClick={() => vote(p.id, o.id)}
                    >
                      <span className="vsgov-poll-bar" style={{ width: `${pct}%` }} />
                      <span className="vsgov-poll-label">
                        {mine ? "● " : ""}
                        {o.label}
                      </span>
                      <span className="vsgov-poll-pct">
                        {o.votes} · {pct}%
                      </span>
                    </button>
                    {voters.length > 0 && (
                      <div className="vsgov-poll-voters">
                        {voters.map((u) => (
                          <span key={u.id} className="vsgov-voter-chip">
                            {u.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="vsgov-poll-foot">
              <span>{total} {total === 1 ? "vote" : "votes"}</span>
              {canManage && open && (
                <button
                  className="vsgov-poll-close"
                  disabled={busy === `c-${p.id}`}
                  onClick={() => closePoll(p.id)}
                >
                  Close poll
                </button>
              )}
            </div>
          </div>
        );
      })}

      {canManage && (
        formOpen ? (
          <div className="vsgov-poll-form">
            <Input
              label="Poll title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. New gym vendor"
            />
            <Input
              label="Question (optional)"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="What should we decide?"
            />
            {options.map((o, i) => (
              <div key={i} className="vsgov-poll-optrow">
                <Input
                  value={o}
                  onChange={(e) =>
                    setOptions((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))
                  }
                  placeholder={`Option ${i + 1}`}
                />
                {options.length > 2 && (
                  <button
                    className="vsgov-poll-optdel"
                    onClick={() => setOptions((prev) => prev.filter((_, j) => j !== i))}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            ))}
            {options.length < 10 && (
              <button
                className="vsgov-poll-addopt"
                onClick={() => setOptions((prev) => [...prev, ""])}
              >
                <Plus size={13} /> Add option
              </button>
            )}
            <div className="vsgov-poll-formactions">
              <Button variant="ghost" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" loading={busy === "create"} onClick={create}>
                Add poll
              </Button>
            </div>
          </div>
        ) : (
          <button className="vsgov-poll-add" onClick={() => setFormOpen(true)}>
            <Plus size={14} /> Add a poll
          </button>
        )
      )}
    </section>
  );
}

function BackLink() {
  return (
    <Link to="/governance" className="vsgov-back">
      <ArrowLeft size={14} /> Governance
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Draft workbench — edit fields, agenda builder, issue notice
// ---------------------------------------------------------------------------

function DraftWorkbench({ meeting, eligible, onChanged }) {
  const [title, setTitle] = useState(meeting.title || "");
  const [description, setDescription] = useState(meeting.description || "");
  const [venue, setVenue] = useState(meeting.venue || "");
  const [when, setWhen] = useState(isoToLocalInput(meeting.scheduled_at));
  const [saving, setSaving] = useState(false);

  // Agenda add form
  const [agKind, setAgKind] = useState("ISSUE");
  const [agIssueId, setAgIssueId] = useState("");
  const [agTitle, setAgTitle] = useState("");
  const [agDetails, setAgDetails] = useState("");
  const [agBusy, setAgBusy] = useState(false);
  const [openIssues, setOpenIssues] = useState([]);

  const [noticeOpen, setNoticeOpen] = useState(false);
  const [noticeBusy, setNoticeBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    governanceApi
      .listIssues({ limit: 100 })
      .then((d) => {
        if (!alive) return;
        const linkable = (d.items || []).filter(
          (i) => !["RESOLVED", "CLOSED"].includes(i.status)
        );
        setOpenIssues(linkable);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const issueOptions = openIssues.filter((i) =>
    agKind === "QUERY" ? i.kind === "QUERY" : i.kind !== "QUERY"
  );

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const next = await governanceApi.patchMeeting(meeting.id, {
        title: title.trim(),
        description: description.trim() || null,
        venue: venue.trim() || null,
        scheduled_at: localInputToIso(when),
      });
      onChanged(next);
      toast.success("Draft updated.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const addAgenda = async () => {
    if (agBusy) return;
    if (agTitle.trim().length < 5 || agDetails.trim().length < 20) return;
    setAgBusy(true);
    try {
      const next = await governanceApi.addAgendaItem(meeting.id, {
        kind: agKind,
        issue_id:
          (agKind === "ISSUE" || agKind === "QUERY") && agIssueId
            ? agIssueId
            : undefined,
        title: agTitle.trim(),
        details: agDetails.trim(),
      });
      onChanged(next);
      setAgTitle("");
      setAgDetails("");
      setAgIssueId("");
      toast.success("Agenda item added.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setAgBusy(false);
    }
  };

  const removeAgenda = async (itemId) => {
    try {
      const next = await governanceApi.removeAgendaItem(meeting.id, itemId);
      onChanged(next);
      toast.success("Agenda item removed.");
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const issueNotice = async () => {
    if (noticeBusy) return;
    setNoticeBusy(true);
    try {
      const next = await governanceApi.issueNotice(meeting.id);
      onChanged(next);
      setNoticeOpen(false);
      toast.success("Notice issued — all members have been notified.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setNoticeBusy(false);
    }
  };

  const agTitleShort = agTitle.trim().length > 0 && agTitle.trim().length < 5;
  const agDetailsShort =
    agDetails.trim().length > 0 && agDetails.trim().length < 20;

  return (
    <section className="vsgov-draft">
      <div className="vsgov-draft-banner">
        This meeting is a <strong>private draft</strong> — set the details,
        build the agenda, then issue the notice to announce it to all members.
      </div>

      <div className="vsgov-draft-grid">
        <Input
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
        />
        <Input
          label="Venue"
          value={venue}
          onChange={(e) => setVenue(e.target.value)}
          placeholder="Required before issuing notice"
        />
        <div className="vsgov-field" style={{ marginTop: 0 }}>
          <label className="vsgov-label" htmlFor="vsgov-draft-when">
            Date &amp; time
          </label>
          <input
            id="vsgov-draft-when"
            type="datetime-local"
            className="vsgov-select"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />
        </div>
        <div className="vsgov-field vsgov-draft-desc" style={{ marginTop: 0 }}>
          <label className="vsgov-label" htmlFor="vsgov-draft-desc">
            Description
          </label>
          <textarea
            id="vsgov-draft-desc"
            className="vsgov-textarea"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      </div>
      <div className="vsgov-draft-saverow">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Save size={14} />}
          onClick={save}
          loading={saving}
        >
          Save draft
        </Button>
      </div>

      <h2 className="vsgov-section-title" style={{ marginTop: 20 }}>
        <ListChecks size={15} /> Agenda ({(meeting.agenda || []).length})
      </h2>
      <AgendaList items={meeting.agenda || []} onRemove={removeAgenda} />

      <div className="vsgov-agenda-form">
        <div className="vsgov-agenda-form-row">
          <div className="vsgov-field" style={{ marginTop: 0, flex: 1 }}>
            <label className="vsgov-label" htmlFor="vsgov-ag-kind">
              Item kind
            </label>
            <select
              id="vsgov-ag-kind"
              className="vsgov-select"
              value={agKind}
              onChange={(e) => {
                setAgKind(e.target.value);
                setAgIssueId("");
              }}
            >
              {AGENDA_KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
          </div>
          {(agKind === "ISSUE" || agKind === "QUERY") && (
            <div className="vsgov-field" style={{ marginTop: 0, flex: 2 }}>
              <label className="vsgov-label" htmlFor="vsgov-ag-issue">
                Link an issue
              </label>
              <select
                id="vsgov-ag-issue"
                className="vsgov-select"
                value={agIssueId}
                onChange={(e) => {
                  setAgIssueId(e.target.value);
                  const sel = issueOptions.find((i) => i.id === e.target.value);
                  if (sel && !agTitle.trim()) setAgTitle(sel.title);
                }}
              >
                <option value="">— No linked issue —</option>
                {issueOptions.map((i) => (
                  <option key={i.id} value={i.id}>
                    [{(ISSUE_KIND_META[i.kind] || {}).label || i.kind}]{" "}
                    {i.title} ({i.support_count} supports)
                  </option>
                ))}
              </select>
              <p className="vsgov-field-hint">
                Linking a threshold-met issue to this notice stops the daily
                board reminders for it.
              </p>
            </div>
          )}
        </div>
        <Input
          label="Item title"
          value={agTitle}
          onChange={(e) => setAgTitle(e.target.value)}
          error={agTitleShort ? "At least 5 characters required" : undefined}
          placeholder="e.g. Approval of FY26-27 maintenance budget"
        />
        <div className="vsgov-field">
          <label className="vsgov-label" htmlFor="vsgov-ag-details">
            Details
          </label>
          <textarea
            id="vsgov-ag-details"
            className="vsgov-textarea"
            rows={3}
            value={agDetails}
            onChange={(e) => setAgDetails(e.target.value)}
            placeholder="What exactly will be discussed or decided under this item?"
          />
          <p className={`vsgov-field-hint ${agDetailsShort ? "error" : ""}`}>
            Minimum 20 characters — every agenda item must be detailed.
          </p>
        </div>
        <div>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={addAgenda}
            loading={agBusy}
            disabled={agTitle.trim().length < 5 || agDetails.trim().length < 20}
          >
            Add agenda item
          </Button>
        </div>
      </div>

      <div className="vsgov-notice-row">
        <Button
          variant="primary"
          leftIcon={<Megaphone size={15} />}
          onClick={() => setNoticeOpen(true)}
        >
          Issue notice
        </Button>
        <span className="vsgov-field-hint">
          Notifies every eligible member in-app and by WhatsApp with RSVP
          buttons. Requires a future date, venue and at least one agenda item.
        </span>
      </div>

      <Dialog
        open={noticeOpen}
        onOpenChange={setNoticeOpen}
        title="Issue meeting notice?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setNoticeOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={issueNotice} loading={noticeBusy}>
              Issue notice
            </Button>
          </>
        }
      >
        <p className="vsgov-confirm-text">
          This notifies all <strong>{eligible}</strong> eligible members
          in-app and by WhatsApp RSVP. The agenda is locked once the notice is
          out, and every linked issue moves to <strong>Scheduled</strong>.
        </p>
      </Dialog>
    </section>
  );
}

function AgendaList({ items, onRemove }) {
  if (!items || items.length === 0) {
    return (
      <p className="vsgov-field-hint">
        No agenda items yet. A notice cannot be issued without at least one.
      </p>
    );
  }
  return (
    <ol className="vsgov-agenda">
      {items.map((it, idx) => (
        <li key={it.id} className="vsgov-agenda-item">
          <span className="vsgov-agenda-pos">{it.position ?? idx + 1}</span>
          <div className="vsgov-agenda-body">
            <div className="vsgov-agenda-toprow">
              <Pill variant="neutral">{it.kind}</Pill>
              <span className="vsgov-agenda-title">{it.title}</span>
            </div>
            {it.details && <p className="vsgov-agenda-details">{it.details}</p>}
            {it.issue && (
              <Link
                to={`/governance/issues/${it.issue.id}`}
                className="vsgov-agenda-issue"
              >
                <Gavel size={12} />{" "}
                {(ISSUE_KIND_META[it.issue.kind] || {}).label || it.issue.kind}:{" "}
                {it.issue.title} ({it.issue.support_count} supports)
              </Link>
            )}
          </div>
          {onRemove && (
            <button
              type="button"
              className="vsgov-agenda-remove"
              aria-label={`Remove agenda item ${it.title}`}
              onClick={() => onRemove(it.id)}
            >
              <Trash2 size={15} />
            </button>
          )}
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// RSVP
// ---------------------------------------------------------------------------

function RsvpSection({ meeting, canRsvp, onChanged }) {
  const [busy, setBusy] = useState("");
  const my = meeting.my_rsvp;

  const respond = async (response) => {
    if (busy) return;
    setBusy(response);
    try {
      const next = await governanceApi.rsvp(meeting.id, response);
      onChanged(next);
      toast.success(
        response === "YES"
          ? "See you there — RSVP recorded."
          : "RSVP recorded."
      );
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const rsvps = meeting.rsvps || [];

  return (
    <section className="vsgov-section">
      <h2 className="vsgov-section-title">
        <UsersIcon size={15} /> RSVP
      </h2>

      {canRsvp && (
        <div className="vsgov-rsvp-cta">
          <span className="vsgov-rsvp-q">
            Will you attend?
            {my?.stale && (
              <span className="vsgov-rsvp-stale-badge">
                Meeting was rescheduled — please reconfirm
              </span>
            )}
          </span>
          <div className="vsgov-rsvp-buttons">
            <button
              type="button"
              className={`vsgov-rsvp-btn yes ${
                my?.response === "YES" && !my?.stale ? "active" : ""
              } ${my?.response === "YES" && my?.stale ? "stale" : ""}`}
              onClick={() => respond("YES")}
              disabled={!!busy}
            >
              {busy === "YES" ? "…" : "Yes, I'll attend"}
            </button>
            <button
              type="button"
              className={`vsgov-rsvp-btn no ${
                my?.response === "NO" && !my?.stale ? "active" : ""
              } ${my?.response === "NO" && my?.stale ? "stale" : ""}`}
              onClick={() => respond("NO")}
              disabled={!!busy}
            >
              {busy === "NO" ? "…" : "No, I can't"}
            </button>
          </div>
        </div>
      )}

      <div className="vsgov-rsvp-tallywrap">
        <RsvpTallyBar rsvp={meeting.rsvp} />
        {meeting.rsvp?.stale > 0 && (
          <p className="vsgov-field-hint">
            {meeting.rsvp.stale} answer{meeting.rsvp.stale === 1 ? "" : "s"}{" "}
            predate the reschedule and await reconfirmation.
          </p>
        )}
      </div>

      {rsvps.length > 0 && (
        <div className="vsgov-tablewrap">
          <table className="vsgov-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Response</th>
                <th>Channel</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {rsvps.map((r) => (
                <tr key={r.id}>
                  <td>
                    <span className="vsgov-table-member">
                      <Avatar name={r.name} imageUrl={r.avatar_url} size={24} />
                      {r.name}
                    </span>
                  </td>
                  <td>
                    <Pill variant={r.response === "YES" ? "success" : "danger"}>
                      {r.response === "YES" ? "Yes" : "No"}
                    </Pill>{" "}
                    {r.stale && <Pill variant="warning">Reconfirm</Pill>}
                  </td>
                  <td>
                    <Pill variant="neutral">
                      {RSVP_CHANNEL_LABELS[r.channel] || r.channel}
                    </Pill>
                  </td>
                  <td className="vsgov-table-when">
                    {formatDateTime(r.responded_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Board controls — reschedule / mark held / cancel
// ---------------------------------------------------------------------------

function BoardControls({ meeting, onChanged }) {
  const [reschedOpen, setReschedOpen] = useState(false);
  const [reschedWhen, setReschedWhen] = useState("");
  const [reschedReason, setReschedReason] = useState("");
  const [heldOpen, setHeldOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [busy, setBusy] = useState("");

  const run = async (key, fn, successMsg, closeFn) => {
    if (busy) return;
    setBusy(key);
    try {
      const next = await fn();
      onChanged(next);
      if (closeFn) closeFn();
      toast.success(successMsg);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="vsgov-section">
      <h2 className="vsgov-section-title">
        <Gavel size={15} /> Board controls
      </h2>
      <div className="vsgov-controls">
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<CalendarClock size={14} />}
          onClick={() => {
            setReschedWhen(isoToLocalInput(meeting.scheduled_at));
            setReschedReason("");
            setReschedOpen(true);
          }}
        >
          Reschedule
        </Button>
        <Button
          variant="primary"
          size="sm"
          leftIcon={<CheckCircle2 size={14} />}
          onClick={() => setHeldOpen(true)}
        >
          Mark held
        </Button>
        <Button
          variant="destructive"
          size="sm"
          leftIcon={<Ban size={14} />}
          onClick={() => {
            setCancelReason("");
            setCancelOpen(true);
          }}
        >
          Cancel meeting
        </Button>
      </div>

      {/* Reschedule */}
      <Dialog
        open={reschedOpen}
        onOpenChange={setReschedOpen}
        title="Reschedule meeting"
        footer={
          <>
            <Button variant="secondary" onClick={() => setReschedOpen(false)}>
              Back
            </Button>
            <Button
              variant="primary"
              loading={busy === "resched"}
              disabled={!reschedWhen || reschedReason.trim().length < 5}
              onClick={() =>
                run(
                  "resched",
                  () =>
                    governanceApi.rescheduleMeeting(meeting.id, {
                      scheduled_at: localInputToIso(reschedWhen),
                      reason: reschedReason.trim(),
                    }),
                  "Meeting rescheduled — members re-notified and asked to reconfirm.",
                  () => setReschedOpen(false)
                )
              }
            >
              Reschedule
            </Button>
          </>
        }
      >
        <div className="vsgov-field" style={{ marginTop: 0 }}>
          <label className="vsgov-label" htmlFor="vsgov-resched-when">
            New date &amp; time
          </label>
          <input
            id="vsgov-resched-when"
            type="datetime-local"
            className="vsgov-select"
            value={reschedWhen}
            onChange={(e) => setReschedWhen(e.target.value)}
          />
        </div>
        <div className="vsgov-field">
          <label className="vsgov-label" htmlFor="vsgov-resched-reason">
            Reason
          </label>
          <textarea
            id="vsgov-resched-reason"
            className="vsgov-textarea"
            rows={3}
            value={reschedReason}
            onChange={(e) => setReschedReason(e.target.value)}
            placeholder="Why is the meeting moving? Members will see this."
          />
          <p className="vsgov-field-hint">
            Minimum 5 characters. Existing RSVPs are kept but flagged for
            reconfirmation; everyone is re-notified in-app and by WhatsApp.
          </p>
        </div>
      </Dialog>

      {/* Mark held */}
      <Dialog
        open={heldOpen}
        onOpenChange={setHeldOpen}
        title="Mark meeting as held?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setHeldOpen(false)}>
              Back
            </Button>
            <Button
              variant="primary"
              loading={busy === "held"}
              onClick={() =>
                run(
                  "held",
                  () => governanceApi.markHeld(meeting.id),
                  "Meeting marked as held.",
                  () => setHeldOpen(false)
                )
              }
            >
              Mark held
            </Button>
          </>
        }
      >
        <p className="vsgov-confirm-text">
          This records the meeting as held. This cannot be undone.
        </p>
      </Dialog>

      {/* Cancel */}
      <Dialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel meeting"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>
              Back
            </Button>
            <Button
              variant="destructive"
              loading={busy === "cancel"}
              disabled={cancelReason.trim().length < 5}
              onClick={() =>
                run(
                  "cancel",
                  () => governanceApi.cancelMeeting(meeting.id, cancelReason.trim()),
                  "Meeting cancelled.",
                  () => setCancelOpen(false)
                )
              }
            >
              Cancel meeting
            </Button>
          </>
        }
      >
        <div className="vsgov-field" style={{ marginTop: 0 }}>
          <label className="vsgov-label" htmlFor="vsgov-cancel-reason">
            Reason (recorded in the audit trail)
          </label>
          <textarea
            id="vsgov-cancel-reason"
            className="vsgov-textarea"
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </div>
      </Dialog>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Resolutions
//
// Two kinds (see docs/specs/governance.md §2.7):
//  • Circulated (origin NOTICE) — tabled while the meeting is a draft, sent to
//    members with the notice, voted online. Shown inside the invitation.
//  • Floor (origin FLOOR) — raised during the meeting and decided in the room
//    by a show of hands; the board records the tally, no online voting.
// ---------------------------------------------------------------------------

function ResolutionCard({ r, canVote, canDecide, busy, onVote, onDecide }) {
  const meta =
    RESOLUTION_STATUS_META[r.status] || { label: r.status, pill: "neutral" };
  const originMeta = RESOLUTION_ORIGIN_META[r.origin];
  const open = r.status === "PROPOSED";
  return (
    <div className="vsgov-res-card">
      <div className="vsgov-card-pills">
        <Pill variant={meta.pill}>{meta.label}</Pill>
        {originMeta && <Pill variant={originMeta.pill}>{originMeta.label}</Pill>}
        {r.issue_id && (
          <Link
            to={`/governance/issues/${r.issue_id}`}
            className="vsgov-resolution-meeting"
          >
            <Gavel size={12} /> Linked issue
          </Link>
        )}
      </div>
      <h3 className="vsgov-card-title">{r.title}</h3>
      <p className="vsgov-resolution-text">{r.text}</p>

      <div className="vsgov-res-tallies">
        <span className="for">
          <strong>{r.votes_for}</strong> for
        </span>
        <span className="against">
          <strong>{r.votes_against}</strong> against
        </span>
        <span className="abstain">
          <strong>{r.votes_abstain}</strong> abstained
        </span>
        {r.decided_at && (
          <span className="decided">Decided {formatDateTime(r.decided_at)}</span>
        )}
      </div>

      {(r.voters || []).length > 0 && (
        <div className="vsgov-res-voters">
          {[
            ["FOR", "For", "for"],
            ["AGAINST", "Against", "against"],
            ["ABSTAIN", "Abstained", "abstain"],
          ].map(([v, label, cls]) => {
            const group = (r.voters || []).filter((u) => u.vote === v);
            if (group.length === 0) return null;
            return (
              <div key={v} className={`vsgov-res-voter-group ${cls}`}>
                <span className="vsgov-res-voter-label">{label}</span>
                {group.map((u) => (
                  <span key={u.id} className="vsgov-voter-chip">
                    {u.name}
                  </span>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {open && canVote && (
        <div className="vsgov-vote-row">
          {[
            ["FOR", "For"],
            ["AGAINST", "Against"],
            ["ABSTAIN", "Abstain"],
          ].map(([v, label]) => (
            <button
              key={v}
              type="button"
              className={`vsgov-vote-btn ${v.toLowerCase()} ${
                r.my_vote === v ? "active" : ""
              }`}
              disabled={!!busy}
              onClick={() => onVote(r.id, v)}
            >
              {busy === `vote-${r.id}-${v}` ? "…" : label}
            </button>
          ))}
          {r.my_vote && (
            <span className="vsgov-field-hint">
              Your vote: {r.my_vote} (changeable until decided)
            </span>
          )}
        </div>
      )}

      {open && canDecide && (
        <div className="vsgov-res-decide">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Gavel size={14} />}
            onClick={() => onDecide(r)}
          >
            Record outcome
          </Button>
        </div>
      )}
    </div>
  );
}

// Resolutions circulated with the notice. mode="draft" → board tabling form
// (before notice); mode="notice" → a block inside the invitation with online
// voting once the notice is out.
function CirculatedResolutions({ meeting, me, onChanged, mode }) {
  const circulated = (meeting.resolutions || []).filter(
    (r) => r.origin === "NOTICE"
  );
  const isDraft = meeting.status === "DRAFT";
  const votingOpen = ["NOTICE_ISSUED", "RESCHEDULED", "HELD"].includes(
    meeting.status
  );
  const canPropose = !!me.is_board && isDraft;
  const canVote = !!me.is_member && votingOpen;
  const canDecide = !!me.is_board && votingOpen;

  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [issueId, setIssueId] = useState("");
  const [busy, setBusy] = useState("");
  const [decideFor, setDecideFor] = useState(null);

  const agendaIssues = (meeting.agenda || []).filter((a) => a.issue);
  const titleShort = title.trim().length > 0 && title.trim().length < 8;
  const textShort = text.trim().length > 0 && text.trim().length < 30;

  const propose = async () => {
    if (busy) return;
    if (title.trim().length < 8 || text.trim().length < 30) return;
    setBusy("propose");
    try {
      const next = await governanceApi.proposeResolution(meeting.id, {
        title: title.trim(),
        text: text.trim(),
        issue_id: issueId || undefined,
      });
      onChanged(next);
      setTitle("");
      setText("");
      setIssueId("");
      setFormOpen(false);
      toast.success("Resolution added — it will be circulated with the notice.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const vote = async (rid, v) => {
    if (busy) return;
    setBusy(`vote-${rid}-${v}`);
    try {
      const next = await governanceApi.voteResolution(rid, v);
      onChanged(next);
      toast.success("Vote recorded.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const decide = async (rid) => {
    if (busy) return;
    setBusy(`decide-${rid}`);
    try {
      const next = await governanceApi.decideResolution(rid);
      onChanged(next);
      setDecideFor(null);
      toast.success("Outcome recorded.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy("");
    }
  };

  const inner = (
    <>
      {mode === "draft" ? (
        <div className="vsgov-res-head">
          <h2 className="vsgov-section-title" style={{ margin: 0 }}>
            <FileSignature size={15} /> Resolutions to circulate ({circulated.length})
          </h2>
          {canPropose && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Plus size={14} />}
              onClick={() => setFormOpen((v) => !v)}
            >
              Propose resolution
            </Button>
          )}
        </div>
      ) : (
        circulated.length > 0 && (
          <h3 className="vsgov-subhead">
            <FileSignature size={14} /> Resolutions for your vote
          </h3>
        )
      )}

      {canPropose && (
        <p className="vsgov-field-hint" style={{ marginTop: 0 }}>
          Resolutions must be tabled now, while the meeting is a draft — they are
          circulated to members with the notice. Once the notice is issued they
          are locked, and members vote online.
        </p>
      )}

      {canPropose && formOpen && (
        <div className="vsgov-res-form">
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={titleShort ? "At least 8 characters required" : undefined}
            placeholder="e.g. Resolution to adopt the FY26-27 budget"
          />
          <div className="vsgov-field">
            <label className="vsgov-label" htmlFor="vsgov-res-text">
              Full text
            </label>
            <textarea
              id="vsgov-res-text"
              className="vsgov-textarea"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="The exact wording members are voting on…"
            />
            <p className={`vsgov-field-hint ${textShort ? "error" : ""}`}>
              Minimum 30 characters — this text becomes binding if passed.
            </p>
          </div>
          {agendaIssues.length > 0 && (
            <div className="vsgov-field">
              <label className="vsgov-label" htmlFor="vsgov-res-issue">
                Link to agenda issue (optional)
              </label>
              <select
                id="vsgov-res-issue"
                className="vsgov-select"
                value={issueId}
                onChange={(e) => setIssueId(e.target.value)}
              >
                <option value="">— None —</option>
                {agendaIssues.map((a) => (
                  <option key={a.issue.id} value={a.issue.id}>
                    {a.issue.title}
                  </option>
                ))}
              </select>
              <p className="vsgov-field-hint">
                A linked issue is marked Resolved when this passes.
              </p>
            </div>
          )}
          <div>
            <Button
              variant="primary"
              size="sm"
              onClick={propose}
              loading={busy === "propose"}
              disabled={title.trim().length < 8 || text.trim().length < 30}
            >
              Add resolution
            </Button>
          </div>
        </div>
      )}

      {circulated.length === 0
        ? mode === "draft" && (
            <p className="vsgov-field-hint">
              None yet. Resolutions you add here are circulated with the notice.
            </p>
          )
        : (
          <div className="vsgov-res-list">
            {circulated.map((r) => (
              <ResolutionCard
                key={r.id}
                r={r}
                canVote={canVote}
                canDecide={canDecide}
                busy={busy}
                onVote={vote}
                onDecide={setDecideFor}
              />
            ))}
          </div>
        )}

      <Dialog
        open={!!decideFor}
        onOpenChange={(v) => !v && setDecideFor(null)}
        title="Record the outcome?"
        footer={
          <>
            <Button variant="secondary" onClick={() => setDecideFor(null)}>
              Back
            </Button>
            <Button
              variant="primary"
              loading={busy === `decide-${decideFor?.id}`}
              onClick={() => decideFor && decide(decideFor.id)}
            >
              Record outcome
            </Button>
          </>
        }
      >
        {decideFor && (
          <p className="vsgov-confirm-text">
            Close voting on <strong>{decideFor.title}</strong> and record the
            outcome from the current tally ({decideFor.votes_for} for,{" "}
            {decideFor.votes_against} against, {decideFor.votes_abstain}{" "}
            abstained). Votes can no longer be changed after this.
          </p>
        )}
      </Dialog>
    </>
  );

  if (mode === "draft") {
    if (circulated.length === 0 && !canPropose) return null;
    return <section className="vsgov-section">{inner}</section>;
  }
  if (circulated.length === 0) return null;
  return <div className="vsgov-notice-block">{inner}</div>;
}

// Resolutions raised from the floor during the meeting. Board records the
// show-of-hands tally; the outcome is settled immediately (no online voting).
function FloorResolutions({ meeting, me, onChanged }) {
  const floor = (meeting.resolutions || []).filter((r) => r.origin === "FLOOR");
  const canRecord =
    !!me.is_board &&
    ["NOTICE_ISSUED", "RESCHEDULED"].includes(meeting.status);

  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [issueId, setIssueId] = useState("");
  const [vFor, setVFor] = useState("");
  const [vAgainst, setVAgainst] = useState("");
  const [vAbstain, setVAbstain] = useState("");
  const [busy, setBusy] = useState(false);

  const agendaIssues = (meeting.agenda || []).filter((a) => a.issue);
  const titleShort = title.trim().length > 0 && title.trim().length < 8;
  const textShort = text.trim().length > 0 && text.trim().length < 30;

  if (floor.length === 0 && !canRecord) return null;

  const reset = () => {
    setTitle("");
    setText("");
    setIssueId("");
    setVFor("");
    setVAgainst("");
    setVAbstain("");
  };

  const record = async () => {
    if (busy) return;
    if (title.trim().length < 8 || text.trim().length < 30) return;
    const f = parseInt(vFor, 10);
    const a = parseInt(vAgainst, 10);
    const ab = vAbstain === "" ? 0 : parseInt(vAbstain, 10);
    if (Number.isNaN(f) || Number.isNaN(a)) {
      toast.error("Enter the for and against counts.");
      return;
    }
    setBusy(true);
    try {
      const next = await governanceApi.recordFloorResolution(meeting.id, {
        title: title.trim(),
        text: text.trim(),
        issue_id: issueId || undefined,
        votes_for: f,
        votes_against: a,
        votes_abstain: ab,
      });
      onChanged(next);
      reset();
      setFormOpen(false);
      toast.success("In-meeting resolution recorded.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="vsgov-section">
      <div className="vsgov-res-head">
        <h2 className="vsgov-section-title" style={{ margin: 0 }}>
          <Gavel size={15} /> Resolutions tabled in the meeting ({floor.length})
        </h2>
        {canRecord && (
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={() => setFormOpen((v) => !v)}
          >
            Record in-meeting resolution
          </Button>
        )}
      </div>

      {canRecord && (
        <p className="vsgov-field-hint" style={{ marginTop: 0 }}>
          For motions raised from the floor and decided in the room by a show of
          hands. Enter the counts as recorded — these are not voted online, and
          the outcome is settled immediately.
        </p>
      )}

      {canRecord && formOpen && (
        <div className="vsgov-res-form">
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={titleShort ? "At least 8 characters required" : undefined}
            placeholder="e.g. Emergency tree trimming before the monsoon"
          />
          <div className="vsgov-field">
            <label className="vsgov-label" htmlFor="vsgov-floor-text">
              Full text
            </label>
            <textarea
              id="vsgov-floor-text"
              className="vsgov-textarea"
              rows={4}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="The exact wording put to the meeting…"
            />
            <p className={`vsgov-field-hint ${textShort ? "error" : ""}`}>
              Minimum 30 characters — this text becomes binding if passed.
            </p>
          </div>
          {agendaIssues.length > 0 && (
            <div className="vsgov-field">
              <label className="vsgov-label" htmlFor="vsgov-floor-issue">
                Link to agenda issue (optional)
              </label>
              <select
                id="vsgov-floor-issue"
                className="vsgov-select"
                value={issueId}
                onChange={(e) => setIssueId(e.target.value)}
              >
                <option value="">— None —</option>
                {agendaIssues.map((a) => (
                  <option key={a.issue.id} value={a.issue.id}>
                    {a.issue.title}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="vsgov-floor-counts">
            <Input
              label="For"
              type="number"
              min="0"
              value={vFor}
              onChange={(e) => setVFor(e.target.value)}
            />
            <Input
              label="Against"
              type="number"
              min="0"
              value={vAgainst}
              onChange={(e) => setVAgainst(e.target.value)}
            />
            <Input
              label="Abstain"
              type="number"
              min="0"
              value={vAbstain}
              onChange={(e) => setVAbstain(e.target.value)}
            />
          </div>
          <p className="vsgov-field-hint">
            Decided immediately — passes if “for” exceeds “against”.
          </p>
          <div>
            <Button
              variant="primary"
              size="sm"
              onClick={record}
              loading={busy}
              disabled={
                title.trim().length < 8 ||
                text.trim().length < 30 ||
                vFor === "" ||
                vAgainst === ""
              }
            >
              Record outcome
            </Button>
          </div>
        </div>
      )}

      {floor.length === 0 ? (
        <p className="vsgov-field-hint">No floor resolutions recorded yet.</p>
      ) : (
        <div className="vsgov-res-list">
          {floor.map((r) => (
            <ResolutionCard
              key={r.id}
              r={r}
              canVote={false}
              canDecide={false}
              busy={null}
              onVote={() => {}}
              onDecide={() => {}}
            />
          ))}
        </div>
      )}
    </section>
  );
}
